/**
 * The picture-processing steps the image tracer is built from, over black-and-white pixels.
 *
 * Pure and canvas-free, so each can be tested with a drawing made pixel by pixel. Nothing here
 * knows what a floorplan is: that belongs to `image-trace`.
 */

/** A black-and-white picture: 1 ink, 0 paper, row by row. */
export interface Bitmap {
  readonly width: number;
  readonly height: number;
  readonly data: Uint8Array;
}

export type Pixel = [number, number];

/** One connected patch of pixels, with what the tracer needs to know about its shape. */
export interface Region {
  readonly label: number;
  area: number;
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
  sumX: number;
  sumY: number;
  sumXX: number;
  sumYY: number;
  sumXY: number;
  touchesEdge: boolean;
}

export interface Labelled {
  /** The region each pixel belongs to, or -1 for a pixel of the other colour. */
  readonly labels: Int32Array;
  readonly regions: Region[];
}

/** The smallest box a patch fits in, turned to suit it. */
export interface OrientedBox {
  readonly centre: Pixel;
  /** The direction of the long side, in degrees, as the image is drawn (y down). */
  readonly angleDegrees: number;
  readonly length: number;
  readonly breadth: number;
  /** How much of the box the patch covers: 1 for a rectangle, about 0.79 for a circle. */
  readonly fill: number;
}

const RADIANS = Math.PI / 180;

/**
 * Every pixel set to the most (`dilate`) or least (`erode`) of the square of side `2r + 1`
 * around it. Done a row and then a column at a time, which gives the same square for a
 * fraction of the work. Beyond the picture counts as paper.
 */
const morphology = (bitmap: Bitmap, radius: number, erode: boolean): Bitmap => {
  const { width, height, data } = bitmap;

  if (radius <= 0) {
    return { width, height, data: data.slice() };
  }

  const full = (2 * radius) + 1;
  // Eroding keeps a pixel whose whole window is ink; dilating, one whose window has any.
  const least = erode ? full : 1;

  // Along each row, a running count of the ink in the window.
  const across = new Uint8Array(data.length);

  for (let y = 0; y < height; y += 1) {
    const row = y * width;
    let count = 0;

    for (let x = 0; x < Math.min(radius, width); x += 1) count += data[row + x];

    for (let x = 0; x < width; x += 1) {
      if (x + radius < width) count += data[row + x + radius];
      if (x - radius - 1 >= 0) count -= data[row + x - radius - 1];
      across[row + x] = count >= least ? 1 : 0;
    }
  }

  // Down each column, the same, kept for every column at once so the picture is read row by row.
  const out = new Uint8Array(data.length);
  const counts = new Int32Array(width);

  for (let y = 0; y < Math.min(radius, height); y += 1) {
    for (let x = 0; x < width; x += 1) counts[x] += across[(y * width) + x];
  }

  for (let y = 0; y < height; y += 1) {
    const entering = y + radius < height ? (y + radius) * width : -1;
    const leaving = y - radius - 1 >= 0 ? (y - radius - 1) * width : -1;
    const row = y * width;

    for (let x = 0; x < width; x += 1) {
      if (entering >= 0) counts[x] += across[entering + x];
      if (leaving >= 0) counts[x] -= across[leaving + x];
      out[row + x] = counts[x] >= least ? 1 : 0;
    }
  }

  return { width, height, data: out };
};

export const dilate = (bitmap: Bitmap, radius = 1): Bitmap => morphology(bitmap, radius, false);

export const erode = (bitmap: Bitmap, radius = 1): Bitmap => morphology(bitmap, radius, true);

/** Thin strokes and spurs removed, solid shapes kept: erode, then grow back. */
export const opened = (bitmap: Bitmap, radius: number): Bitmap => dilate(erode(bitmap, radius), radius);

/**
 * Small gaps and notches filled, the outline kept: grow, then erode back. Done on the picture with
 * `radius` of paper round it, so what grew past the edge erodes back as it grew: eroding the
 * picture itself counts beyond it as paper, and would eat away ink drawn up to its edge.
 */
export const closed = (bitmap: Bitmap, radius: number): Bitmap => {
  const { width, height, data } = bitmap;
  if (radius <= 0) return { width, height, data: data.slice() };

  const paddedWidth = width + (2 * radius);
  const padded = new Uint8Array(paddedWidth * (height + (2 * radius)));
  for (let y = 0; y < height; y += 1) padded.set(data.subarray(y * width, (y + 1) * width), ((y + radius) * paddedWidth) + radius);

  const shut = erode(dilate({ width: paddedWidth, height: height + (2 * radius), data: padded }, radius), radius);
  const out = new Uint8Array(width * height);
  for (let y = 0; y < height; y += 1) {
    const from = ((y + radius) * paddedWidth) + radius;
    out.set(shut.data.subarray(from, from + width), y * width);
  }
  return { width, height, data: out };
};

/**
 * The connected patches of one colour, each labelled, found by flooding out from every
 * unlabelled pixel. Ink is best joined across corners (`eight`), paper is not: two rooms that
 * touch only at a corner are two rooms.
 */
export const labelRegions = (bitmap: Bitmap, colour: 0 | 1, eight: boolean): Labelled => {
  const { width, height, data } = bitmap;
  const labels = new Int32Array(data.length).fill(-1);
  const regions: Region[] = [];
  // Where runs still to be followed start. A run is pushed once per run beside it, so this is
  // bounded by the picture's size, but is usually a small fraction of it.
  let seeds = new Int32Array(Math.max(64, width * 4));
  const last = width - 1;
  // The sum of the squares from 0 to n.
  const squares = (n: number): number => (n * (n + 1) * ((2 * n) + 1)) / 6;

  for (let start = 0; start < data.length; start += 1) {
    if (data[start] === colour && labels[start] === -1) {
      const label = regions.length;
      let area = 0;
      let minX = width;
      let maxX = 0;
      let minY = height;
      let maxY = 0;
      let sumX = 0;
      let sumY = 0;
      let sumXX = 0;
      let sumYY = 0;
      let sumXY = 0;
      let touchesEdge = false;

      let top = 0;
      seeds[top] = start;
      top += 1;

      // A run at a time: from a pixel of the patch, as far as the patch goes along its row, all
      // labelled together and counted in closed form; then the runs touching it above and below
      // are followed. Most of what reading an image costs is here.
      while (top > 0) {
        top -= 1;
        const seed = seeds[top];

        if (labels[seed] === -1) {
          const y = Math.floor(seed / width);
          const row = y * width;
          let left = seed - row;
          let right = left;
          while (left > 0 && data[row + left - 1] === colour && labels[row + left - 1] === -1) left -= 1;
          while (right < last && data[row + right + 1] === colour && labels[row + right + 1] === -1) right += 1;

          labels.fill(label, row + left, row + right + 1);
          const count = (right - left) + 1;
          const runX = ((left + right) * count) / 2;
          area += count;
          sumX += runX;
          sumXX += squares(right) - (left > 0 ? squares(left - 1) : 0);
          sumY += y * count;
          sumYY += y * y * count;
          sumXY += y * runX;
          if (left < minX) minX = left;
          if (right > maxX) maxX = right;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
          if (left === 0 || right === last || y === 0 || y === height - 1) touchesEdge = true;

          // The rows above and below, as far as the run reaches — a pixel further each way when
          // corners join.
          const from = eight ? Math.max(0, left - 1) : left;
          const to = eight ? Math.min(last, right + 1) : right;

          for (let beside = y - 1; beside <= y + 1; beside += 2) {
            if (beside >= 0 && beside < height) {
              const besideRow = beside * width;
              let inRun = false;

              for (let x = from; x <= to; x += 1) {
                const index = besideRow + x;
                const joins = data[index] === colour && labels[index] === -1;
                if (joins && !inRun) {
                  if (top === seeds.length) {
                    const grown = new Int32Array(seeds.length * 2);
                    grown.set(seeds);
                    seeds = grown;
                  }
                  seeds[top] = index;
                  top += 1;
                }
                inRun = joins;
              }
            }
          }
        }
      }

      regions.push({
        label, area, minX, maxX, minY, maxY, sumX, sumY, sumXX, sumYY, sumXY, touchesEdge,
      });
    }
  }

  return { labels, regions };
};

export const boxWidthOf = (region: Region): number => region.maxX - region.minX + 1;

export const boxHeightOf = (region: Region): number => region.maxY - region.minY + 1;

export const centroidOf = (region: Region): Pixel => [region.sumX / region.area, region.sumY / region.area];

/**
 * The turned box that fits a patch best. The patch's own axis (from its second moments) is
 * tried, and so are level and 45 degrees from it, because the axis of a near-square says
 * nothing useful and a square read at 45 degrees looks half empty.
 */
export const orientedBoxOf = (labels: Int32Array, width: number, region: Region): OrientedBox => {
  const [meanX, meanY] = centroidOf(region);
  const varianceX = (region.sumXX / region.area) - (meanX * meanX);
  const varianceY = (region.sumYY / region.area) - (meanY * meanY);
  const covariance = (region.sumXY / region.area) - (meanX * meanY);
  const axis = (0.5 * Math.atan2(2 * covariance, varianceX - varianceY)) / RADIANS;

  const measure = (angleDegrees: number): OrientedBox => {
    const cos = Math.cos(angleDegrees * RADIANS);
    const sin = Math.sin(angleDegrees * RADIANS);
    let minU = Infinity;
    let maxU = -Infinity;
    let minV = Infinity;
    let maxV = -Infinity;

    for (let y = region.minY; y <= region.maxY; y += 1) {
      for (let x = region.minX; x <= region.maxX; x += 1) {
        if (labels[(y * width) + x] === region.label) {
          const u = ((x - meanX) * cos) + ((y - meanY) * sin);
          const v = ((y - meanY) * cos) - ((x - meanX) * sin);
          if (u < minU) minU = u;
          if (u > maxU) maxU = u;
          if (v < minV) minV = v;
          if (v > maxV) maxV = v;
        }
      }
    }

    const along = (maxU - minU) + 1;
    const across = (maxV - minV) + 1;
    const middleU = (minU + maxU) / 2;
    const middleV = (minV + maxV) / 2;
    const centre: Pixel = [meanX + (middleU * cos) - (middleV * sin), meanY + (middleU * sin) + (middleV * cos)];
    const long = along >= across;

    return {
      centre,
      angleDegrees: long ? angleDegrees : angleDegrees + 90,
      length: Math.max(along, across),
      breadth: Math.min(along, across),
      fill: region.area / (along * across),
    };
  };

  return [axis, 0, axis + 45]
    .map(measure)
    .reduce((best, candidate) => (candidate.fill > best.fill + 0.02 ? candidate : best));
};

/** The eight neighbours, clockwise from west, as the picture is drawn (y down). */
const AROUND: Pixel[] = [[-1, 0], [-1, -1], [0, -1], [1, -1], [1, 0], [1, 1], [0, 1], [-1, 1]];

const directionOf = (dx: number, dy: number): number => AROUND.findIndex(([ax, ay]) => ax === dx && ay === dy);

/**
 * The outer edge of a patch, walked pixel by pixel (Moore neighbour tracing), from its top-left
 * pixel round and back. Stops on returning to the start the way it first left it, so a patch
 * with a one-pixel neck is walked all the way round rather than half.
 */
export const traceBoundary = (
  inside: (x: number, y: number) => boolean,
  start: Pixel,
  limit: number,
): Pixel[] => {
  const outline: Pixel[] = [start];
  let current: Pixel = start;
  // Nothing of the patch is west of its top-left pixel, so that is where the walk looks from.
  let backtrack = 0;
  let firstDirection = -1;

  for (let step = 0; step < limit; step += 1) {
    let found = -1;

    for (let turn = 1; turn <= 8 && found < 0; turn += 1) {
      const direction = (backtrack + turn) % 8;
      if (inside(current[0] + AROUND[direction][0], current[1] + AROUND[direction][1])) {
        found = direction;
      }
    }

    if (found < 0) {
      return outline;
    }

    if (current[0] === start[0] && current[1] === start[1]) {
      if (firstDirection === found) {
        return outline.slice(0, -1);
      }

      if (firstDirection < 0) {
        firstDirection = found;
      }
    }

    // The last outside neighbour looked at, seen from the pixel being stepped to.
    const behind = AROUND[(found + 7) % 8];
    const next: Pixel = [current[0] + AROUND[found][0], current[1] + AROUND[found][1]];
    backtrack = directionOf((current[0] + behind[0]) - next[0], (current[1] + behind[1]) - next[1]);
    current = next;
    outline.push(current);
  }

  return outline;
};

/** The top-left pixel of a labelled patch: where `traceBoundary` starts. */
export const topLeftOf = (labels: Int32Array, width: number, region: Region): Pixel => {
  let x = region.minX;
  while (x < region.maxX && labels[(region.minY * width) + x] !== region.label) x += 1;
  return [x, region.minY];
};

/**
 * Lines thinned to a pixel wide down their middle (Zhang and Suen's thinning): each pass peels
 * off the edge pixels that neither end a line nor hold two parts of it together, from the south
 * and east, then from the north and west, until nothing more comes off.
 */
export const thinned = (bitmap: Bitmap): Bitmap => {
  const { width, height } = bitmap;
  // With a pixel of paper round it, so a pixel's neighbours need no test for the edge.
  const stride = width + 2;
  const data = new Uint8Array(stride * (height + 2));
  for (let y = 0; y < height; y += 1) data.set(bitmap.data.subarray(y * width, (y + 1) * width), ((y + 1) * stride) + 1);

  // Only the ink is gone through, and only what is left of it.
  let ink: number[] = [];
  for (let index = 0; index < data.length; index += 1) if (data[index]) ink.push(index);
  const rises = (from: number, to: number): number => (from === 0 && to === 1 ? 1 : 0);
  const peeled: number[] = [];
  let changed = true;

  while (changed) {
    changed = false;
    for (let pass = 0; pass < 2; pass += 1) {
      peeled.length = 0;
      ink.forEach((index) => {
        const [n, ne, e, se] = [data[index - stride], data[index - stride + 1], data[index + 1], data[index + stride + 1]];
        const [s, sw, w, nw] = [data[index + stride], data[index + stride - 1], data[index - 1], data[index - stride - 1]];
        const neighbours = n + ne + e + se + s + sw + w + nw;
        if (neighbours < 2 || neighbours > 6) return;
        // Clockwise from north, how many times paper gives way to ink: once, at the edge of a line.
        const turns = rises(n, ne) + rises(ne, e) + rises(e, se) + rises(se, s) + rises(s, sw) + rises(sw, w) + rises(w, nw) + rises(nw, n);
        const clearOfOneSide = pass === 0 ? !(n && e && s) && !(e && s && w) : !(n && e && w) && !(n && s && w);
        if (turns === 1 && clearOfOneSide) peeled.push(index);
      });
      peeled.forEach((index) => { data[index] = 0; });
      if (peeled.length) {
        changed = true;
        ink = ink.filter((index) => data[index]);
      }
    }
  }

  const out = new Uint8Array(width * height);
  for (let y = 0; y < height; y += 1) out.set(data.subarray(((y + 1) * stride) + 1, ((y + 1) * stride) + 1 + width), y * width);
  return { width, height, data: out };
};

/** Where a line stops, and the way it was heading there: a unit step onward from its end. */
export interface LineEnd {
  readonly at: Pixel;
  readonly heading: Pixel;
}

/**
 * The ends of the lines of a thinned picture, each with the way the line runs into it: from the
 * pixels up to `back` steps along the line behind it. A line only a pixel or two long is a spur
 * thinning left on a corner, not a line that stops.
 */
export const lineEndsOf = (skeleton: Bitmap, back: number): LineEnd[] => {
  const { width, height, data } = skeleton;
  const inkAt = (x: number, y: number): boolean => x >= 0 && y >= 0 && x < width && y < height && data[(y * width) + x] === 1;
  const ends: LineEnd[] = [];

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      if (inkAt(x, y)) {
        // An end: its neighbours all on one side of it, together.
        const around = AROUND.map(([dx, dy]) => inkAt(x + dx, y + dy));
        const neighbours = around.filter(Boolean).length;
        const turns = around.filter((value, index) => !value && around[(index + 1) % 8]).length;

        if (neighbours >= 1 && neighbours <= 3 && turns === 1) {
          // Back along the line from the end, a step at a time, each to what is furthest from it.
          const seen = new Set<number>([(y * width) + x]);
          let current: Pixel = [x, y];
          let steps = 0;
          while (steps < back) {
            const [cx, cy] = current;
            const next = AROUND.map(([dx, dy]): Pixel => [cx + dx, cy + dy])
              .filter(([nx, ny]) => inkAt(nx, ny) && !seen.has((ny * width) + nx));
            if (!next.length) break;
            // Neighbours of the end itself are one step, whichever is taken.
            next.forEach(([nx, ny]) => seen.add((ny * width) + nx));
            [current] = next.sort(([ax, ay], [bx, by]) => Math.hypot(bx - x, by - y) - Math.hypot(ax - x, ay - y));
            steps += 1;
          }

          const [dx, dy] = [x - current[0], y - current[1]];
          const length = Math.hypot(dx, dy);
          if (steps >= Math.min(back, 3) && length > 0) ends.push({ at: [x, y], heading: [dx / length, dy / length] });
        }
      }
    }
  }

  return ends;
};

/**
 * The pixels inside `points` set to 1, in place: every pixel whose middle is inside the outline,
 * counted the even-odd way, so a ring drawn round a hole leaves the hole out.
 */
export const fillPolygon = (bitmap: Bitmap, points: readonly Pixel[]): void => {
  if (points.length < 3) return;
  const { width, height, data } = bitmap;
  const ys = points.map(([, y]) => y);
  const top = Math.max(0, Math.ceil(Math.min(...ys) - 0.5));
  const bottom = Math.min(height - 1, Math.floor(Math.max(...ys) - 0.5));

  for (let y = top; y <= bottom; y += 1) {
    const middle = y + 0.5;
    const crossings: number[] = [];
    for (let index = 0; index < points.length; index += 1) {
      const [ax, ay] = points[index];
      const [bx, by] = points[(index + 1) % points.length];
      if ((ay <= middle && by > middle) || (by <= middle && ay > middle)) {
        crossings.push(ax + (((middle - ay) / (by - ay)) * (bx - ax)));
      }
    }
    crossings.sort((first, second) => first - second);
    for (let index = 0; index + 1 < crossings.length; index += 2) {
      const from = Math.max(0, Math.ceil(crossings[index] - 0.5));
      const to = Math.min(width - 1, Math.floor(crossings[index + 1] - 0.5));
      if (to >= from) data.fill(1, (y * width) + from, (y * width) + to + 1);
    }
  }
};

/**
 * A line from `from` to `to` drawn `reach` pixels thick each side, and as far past each end, in
 * place: what is within `reach` of it, near enough, with square ends that close a corner.
 */
export const strokeSegment = (bitmap: Bitmap, from: Pixel, to: Pixel, reach: number): void => {
  const length = Math.hypot(to[0] - from[0], to[1] - from[1]);
  const [ux, uy] = length > 0 ? [(to[0] - from[0]) / length, (to[1] - from[1]) / length] : [1, 0];
  const [ax, ay] = [ux * reach, uy * reach];
  const [nx, ny] = [-uy * reach, ux * reach];
  fillPolygon(bitmap, [
    [from[0] - ax + nx, from[1] - ay + ny],
    [to[0] + ax + nx, to[1] + ay + ny],
    [to[0] + ax - nx, to[1] + ay - ny],
    [from[0] - ax - nx, from[1] - ay - ny],
  ]);
};

/** Each edge of an outline drawn `reach` pixels thick, in place: the outline, not what it closes. */
export const strokeOutline = (bitmap: Bitmap, points: readonly Pixel[], reach: number, closedRing = true): void => {
  const last = closedRing ? points.length : points.length - 1;
  for (let index = 0; index < last; index += 1) {
    strokeSegment(bitmap, points[index], points[(index + 1) % points.length], reach);
  }
};

/** A piece of ink's outline, and the outlines of the holes in it. */
export interface InkContour {
  /** Round the outside of the ink, clockwise as seen (y down), the first point again at the end. */
  readonly outer: Pixel[];
  /** Round each hole the other way. */
  readonly holes: Pixel[][];
  /** How much ink the piece is, in square pixels: its outline's area less its holes'. */
  readonly area: number;
  /** How far across and down the piece reaches, in pixels. */
  readonly width: number;
  readonly height: number;
}

/** Twice the area a ring closes, positive clockwise as seen (y down). */
const twiceSignedAreaOf = (ring: readonly Pixel[]): number => ring
  .reduce((sum, [x, y], index) => {
    const [nx, ny] = ring[(index + 1) % ring.length];
    return sum + ((x * ny) - (nx * y));
  }, 0);

interface RingBox {
  readonly ring: Pixel[];
  readonly area: number;
  readonly minX: number;
  readonly maxX: number;
  readonly minY: number;
  readonly maxY: number;
}

const ringBoxOf = (ring: Pixel[]): RingBox => {
  const xs = ring.map(([x]) => x);
  const ys = ring.map(([, y]) => y);
  return {
    ring, area: twiceSignedAreaOf(ring) / 2, minX: Math.min(...xs), maxX: Math.max(...xs), minY: Math.min(...ys), maxY: Math.max(...ys),
  };
};

/**
 * Where each cell's outline crosses its edges — top 0, right 1, bottom 2, left 3 — by which of its
 * four corners are ink (top left 8, top right 4, bottom right 2, bottom left 1): from one edge to
 * another, gone round with the ink on the right as seen. Ink on opposite corners only is joined
 * through the middle, as the eye joins a line drawn a pixel at a time corner to corner.
 */
const CELL_STEPS: readonly (readonly number[])[] = ((): number[][] => {
  const crossings: readonly (readonly [number, number][])[] = [
    [], [[3, 2]], [[2, 1]], [[3, 1]], [[0, 1]], [[0, 3], [2, 1]], [[0, 2]], [[0, 3]],
    [[0, 3]], [[0, 2]], [[0, 1], [3, 2]], [[0, 1]], [[3, 1]], [[2, 1]], [[3, 2]], [],
  ];
  // A unit cell: its edges' middles and its corners.
  const middles: Pixel[] = [[0.5, 0], [1, 0.5], [0.5, 1], [0, 0.5]];
  const corners: Pixel[] = [[0, 0], [1, 0], [1, 1], [0, 1]];
  return crossings.map((pairs, cell) => {
    const ink = [8, 4, 2, 1].map((bit) => (Math.floor(cell / bit) % 2 === 1));
    return pairs.flatMap(([first, second]) => {
      const [a, b] = [middles[first], middles[second]];
      const sideOf = ([x, y]: Pixel): number => ((b[0] - a[0]) * (y - a[1])) - ((b[1] - a[1]) * (x - a[0]));
      const right = corners.map((corner, index) => ({ side: sideOf(corner), ink: ink[index] })).filter(({ side }) => side > 0);
      const left = corners.map((corner, index) => ({ side: sideOf(corner), ink: ink[index] })).filter(({ side }) => side < 0);
      // The side with fewer corners is all one: a corner cut off, or half the cell.
      const rightIsInk = right.length <= left.length ? right[0].ink : !left[0].ink;
      return rightIsInk ? [first, second] : [second, first];
    });
  });
})();

/**
 * The outline of every piece of ink, through the middles between ink and paper pixels (marching
 * squares): a line keeps its width as drawn, and a slanting or curved edge follows its slant and
 * its curve instead of the pixels' steps. Each piece comes with the outlines of the holes in it.
 */
export const contoursOf = (bitmap: Bitmap): InkContour[] => {
  const { width, height, data } = bitmap;
  // The pixels' middles, with a row and column of paper all round so every outline closes.
  const columns = width + 2;
  const rows = height + 2;
  const padded = new Uint8Array(columns * rows);
  for (let y = 0; y < height; y += 1) padded.set(data.subarray(y * width, (y + 1) * width), ((y + 1) * columns) + 1);

  // From each crossing, by the grid edge it is on (two a middle: across, then down), the next one
  // along its outline; -1 for none.
  const next = new Int32Array(columns * rows * 2).fill(-1);
  const starts: number[] = [];
  // Only the cells round ink can be crossed: the four with an ink pixel at a corner, each once.
  const looked = new Uint8Array(columns * rows);
  const lookAt = (at: number): void => {
    if (looked[at]) return;
    looked[at] = 1;
    const cell = (padded[at] * 8) + (padded[at + 1] * 4) + (padded[at + columns + 1] * 2) + padded[at + columns];
    const steps = CELL_STEPS[cell];
    if (!steps.length) return;
    // The cell's edges: top, right, bottom, left.
    const edges = [at * 2, ((at + 1) * 2) + 1, (at + columns) * 2, (at * 2) + 1];
    for (let index = 0; index < steps.length; index += 2) {
      next[edges[steps[index]]] = edges[steps[index + 1]];
      starts.push(edges[steps[index]]);
    }
  };
  for (let at = columns; at < padded.length - columns; at += 1) {
    if (padded[at]) {
      lookAt(at - columns - 1);
      lookAt(at - columns);
      lookAt(at - 1);
      lookAt(at);
    }
  }

  // Which piece of ink each outline goes round: the ink beside its first crossing. Ink joined
  // corner to corner is one piece, as the outlines join it.
  const pieces = labelRegions(bitmap, 1, true);
  const pieceOf = (edge: number): number => {
    const middle = Math.floor(edge / 2);
    // Across, the crossing is between this middle and the one to its right; down, the one below.
    const other = middle + (edge % 2 === 0 ? 1 : columns);
    const inked = padded[middle] ? middle : other;
    const [column, row] = [inked % columns, Math.floor(inked / columns)];
    return pieces.labels[((row - 1) * width) + (column - 1)];
  };

  const outers = new Map<number, RingBox>();
  const holes = new Map<number, Pixel[][]>();
  // A ring's corners as they are found, across then down, before they are made points.
  const xs: number[] = [];
  const ys: number[] = [];
  starts.forEach((start) => {
    if (next[start] < 0) return;
    xs.length = 0;
    ys.length = 0;
    let edge = start;
    while (edge >= 0) {
      // Where the crossing is, in pixels: pixel x's middle is at x + 0.5, so padded column c's at c - 0.5.
      const middle = Math.floor(edge / 2);
      const column = middle % columns;
      const row = Math.floor(middle / columns);
      const across = edge % 2 === 0;
      const x = across ? column : column - 0.5;
      const y = across ? row - 0.5 : row;
      // A corner in line with the two before it says nothing: a straight run is its two ends.
      const count = xs.length;
      const inLine = count >= 2
        && ((xs[count - 1] - xs[count - 2]) * (y - ys[count - 1])) === ((ys[count - 1] - ys[count - 2]) * (x - xs[count - 1]));
      if (inLine) {
        xs[count - 1] = x;
        ys[count - 1] = y;
      } else {
        xs.push(x);
        ys.push(y);
      }
      const following = next[edge];
      next[edge] = -1;
      edge = following === start ? -1 : following;
    }
    if (xs.length < 3) return;
    const closedRing: Pixel[] = xs.map((x, index): Pixel => [x, ys[index]]);
    closedRing.push([xs[0], ys[0]]);
    const piece = pieceOf(start);
    // Clockwise as seen round the outside; the other way round a hole.
    if (twiceSignedAreaOf(closedRing) > 0) outers.set(piece, ringBoxOf(closedRing));
    else holes.set(piece, [...(holes.get(piece) ?? []), closedRing]);
  });

  return Array.from(outers, ([piece, outer]) => {
    const inside = holes.get(piece) ?? [];
    return {
      outer: outer.ring,
      holes: inside,
      area: outer.area + inside.reduce((sum, hole) => sum + (twiceSignedAreaOf(hole) / 2), 0),
      width: outer.maxX - outer.minX,
      height: outer.maxY - outer.minY,
    };
  });
};

// Square roots rather than Math.hypot: the same distances, several times faster, and outlines
// kept as drawn ask for a great many.
const distanceToLine = ([x, y]: Pixel, [ax, ay]: Pixel, [bx, by]: Pixel): number => {
  const length = Math.sqrt(((bx - ax) * (bx - ax)) + ((by - ay) * (by - ay)));

  if (length === 0) {
    return Math.sqrt(((x - ax) * (x - ax)) + ((y - ay) * (y - ay)));
  }

  return Math.abs(((by - ay) * x) - ((bx - ax) * y) + (bx * ay) - (by * ax)) / length;
};

/** Fewer points along the same line, keeping every turn wider than `tolerance` (Ramer–Douglas–Peucker). */
export const simplifiedLine = (points: Pixel[], tolerance: number): Pixel[] => {
  if (points.length < 3) return points;

  // Which points are kept, settled a stretch at a time: each stretch keeps the point furthest
  // from the line between its ends, when that is further than `tolerance`, and is split there.
  const kept = new Uint8Array(points.length);
  kept[0] = 1;
  kept[points.length - 1] = 1;
  const stretches: [number, number][] = [[0, points.length - 1]];

  while (stretches.length) {
    const [from, to] = stretches.pop() as [number, number];
    let furthest = 0;
    let furthestIndex = 0;

    for (let index = from + 1; index < to; index += 1) {
      const distance = distanceToLine(points[index], points[from], points[to]);

      if (distance > furthest) {
        furthest = distance;
        furthestIndex = index;
      }
    }

    if (furthest > tolerance) {
      kept[furthestIndex] = 1;
      stretches.push([from, furthestIndex], [furthestIndex, to]);
    }
  }

  return points.filter((_, index) => kept[index] === 1);
};

/**
 * A closed outline simplified the same way. Split at the point furthest from the start first,
 * so both halves are simplified between two real corners and neither end is lost.
 */
export const simplifiedRing = (points: Pixel[], tolerance: number): Pixel[] => {
  if (points.length < 4) return points;

  let far = 0;
  let farIndex = 0;

  points.forEach(([x, y], index) => {
    const distance = Math.hypot(x - points[0][0], y - points[0][1]);

    if (distance > far) {
      far = distance;
      farIndex = index;
    }
  });

  const there = simplifiedLine(points.slice(0, farIndex + 1), tolerance);
  const back = simplifiedLine([...points.slice(farIndex), points[0]], tolerance);
  return [...there.slice(0, -1), ...back.slice(0, -1)];
};

/** Corners that are the same point, or sit on a straight line between their neighbours, left out. */
export const withoutRedundantCorners = (points: Pixel[]): Pixel[] => {
  let current = points;
  let changed = true;

  while (changed && current.length > 3) {
    changed = false;
    const kept: Pixel[] = [];

    for (let index = 0; index < current.length; index += 1) {
      const before = kept.length ? kept[kept.length - 1] : current[current.length - 1];
      const point = current[index];
      const after = current[(index + 1) % current.length];
      const same = point[0] === before[0] && point[1] === before[1];
      const straight = distanceToLine(point, before, after) < 0.01
        && ((point[0] - before[0]) * (after[0] - point[0])) + ((point[1] - before[1]) * (after[1] - point[1])) >= 0;

      if (same || straight) {
        changed = true;
      } else {
        kept.push(point);
      }
    }

    current = kept;
  }

  return current;
};

/**
 * Edges within `degrees` of level or plumb made exactly so, by moving the corner after them.
 * Nothing moves when the drawing's walls are not square: that is a real angle, not a wobble.
 */
export const straightened = (points: Pixel[], degrees: number): Pixel[] => {
  const out = points.map(([x, y]): Pixel => [x, y]);
  const limit = Math.tan(degrees * RADIANS);

  // Twice round: the closing edge moves the first corner, which the first edge has to follow.
  for (let pass = 0; pass < 2; pass += 1) {
    for (let index = 0; index < out.length; index += 1) {
      const current = out[index];
      const next = out[(index + 1) % out.length];
      const dx = next[0] - current[0];
      const dy = next[1] - current[1];
      const [currentX, currentY] = current;

      if (Math.abs(dy) <= Math.abs(dx) * limit) {
        next[1] = currentY;
      } else if (Math.abs(dx) <= Math.abs(dy) * limit) {
        next[0] = currentX;
      }
    }
  }

  return withoutRedundantCorners(out);
};

const rotatedFrame = (width: number, height: number, degrees: number): { width: number; height: number } => {
  const cos = Math.abs(Math.cos(degrees * RADIANS));
  const sin = Math.abs(Math.sin(degrees * RADIANS));

  return {
    width: Math.ceil((width * cos) + (height * sin)),
    height: Math.ceil((width * sin) + (height * cos)),
  };
};

/**
 * The picture turned by `degrees` about its middle (positive turns clockwise as drawn), on a
 * frame large enough to keep every corner.
 */
export const rotateBitmap = (bitmap: Bitmap, degrees: number): Bitmap => {
  const frame = rotatedFrame(bitmap.width, bitmap.height, degrees);
  const cos = Math.cos(degrees * RADIANS);
  const sin = Math.sin(degrees * RADIANS);
  const [fromX, fromY] = [bitmap.width / 2, bitmap.height / 2];
  const [toX, toY] = [frame.width / 2, frame.height / 2];
  const data = new Uint8Array(frame.width * frame.height);

  for (let y = 0; y < frame.height; y += 1) {
    for (let x = 0; x < frame.width; x += 1) {
      const sourceX = Math.round(fromX + ((x - toX) * cos) + ((y - toY) * sin));
      const sourceY = Math.round(fromY - ((x - toX) * sin) + ((y - toY) * cos));

      if (sourceX >= 0 && sourceY >= 0 && sourceX < bitmap.width && sourceY < bitmap.height) {
        data[(y * frame.width) + x] = bitmap.data[(sourceY * bitmap.width) + sourceX];
      }
    }
  }

  return { ...frame, data };
};

/**
 * Where a pixel of the picture `rotateBitmap` turned by `degrees` came from, and back: so what is
 * found on a turned copy can be put where it is on the picture itself.
 */
export interface Turn {
  readonly width: number;
  readonly height: number;
  readonly toSource: (x: number, y: number) => [number, number];
  readonly toTurned: (x: number, y: number) => [number, number];
}

export const turnOf = (width: number, height: number, degrees: number): Turn => {
  const frame = rotatedFrame(width, height, degrees);
  const cos = Math.cos(degrees * RADIANS);
  const sin = Math.sin(degrees * RADIANS);
  const [fromX, fromY] = [width / 2, height / 2];
  const [toX, toY] = [frame.width / 2, frame.height / 2];

  return {
    ...frame,
    toSource: (x, y) => [fromX + ((x - toX) * cos) + ((y - toY) * sin), fromY - ((x - toX) * sin) + ((y - toY) * cos)],
    toTurned: (x, y) => [toX + ((x - fromX) * cos) - ((y - fromY) * sin), toY + ((x - fromX) * sin) + ((y - fromY) * cos)],
  };
};

/** A direction the drawing's lines run in, and how much of their edge runs that way. */
export interface LineDirection {
  /** As drawn (y down), 0 to 180: 0 across the picture, 90 down it. */
  readonly degrees: number;
  readonly share: number;
}

/** The most edge pixels the directions are read from. */
const DIRECTION_SAMPLES = 60000;

/** A direction is one lines are drawn in when this much of its edge lies in long lines. */
const LINED_SHARE = 0.3;

/**
 * The directions the edges of the ink run in, strongest first: each edge pixel's direction from
 * the gradients around it (a structure tensor over a 5 x 5 window, which is steady on a line
 * drawn in pixel steps), counted by how clearly it is one direction — a corner is not. Only
 * peaks holding at least `minShare` of the edge, at least `axisMargin` degrees from level and
 * plumb, and whose edge lies in lines at least `minLine` pixels long, are kept: those are what
 * rows and columns do not already follow.
 */
export const lineDirectionsOf = (
  bitmap: Bitmap,
  {
    minShare = 0.03, axisMargin = 6, most = 4, minLine = 20,
  }: { minShare?: number; axisMargin?: number; most?: number; minLine?: number } = {},
): LineDirection[] => {
  const { width, height, data } = bitmap;
  // The picture with a pixel of paper round it, so the gradients need no test for its edge.
  const paddedWidth = width + 2;
  const padded = new Uint8Array(paddedWidth * (height + 2));
  for (let y = 0; y < height; y += 1) padded.set(data.subarray(y * width, (y + 1) * width), ((y + 1) * paddedWidth) + 1);

  const gx = new Int8Array(width * height);
  const gy = new Int8Array(width * height);
  const edges: number[] = [];

  for (let y = 0; y < height; y += 1) {
    const above = y * paddedWidth;
    const middle = above + paddedWidth;
    const below = middle + paddedWidth;

    // Each neighbour its own variable: this runs for every pixel, and a list made per pixel is slow.
    for (let x = 0; x < width; x += 1) {
      const index = (y * width) + x;
      const nw = padded[above + x];
      const n = padded[above + x + 1];
      const ne = padded[above + x + 2];
      const w = padded[middle + x];
      const e = padded[middle + x + 2];
      const sw = padded[below + x];
      const s = padded[below + x + 1];
      const se = padded[below + x + 2];
      gx[index] = (ne + (2 * e) + se) - (nw + (2 * w) + sw);
      gy[index] = (sw + (2 * s) + se) - (nw + (2 * n) + ne);
      if (data[index] && (!n || !s || !w || !e)) edges.push(index);
    }
  }

  const stride = Math.max(1, Math.ceil(edges.length / DIRECTION_SAMPLES));
  const bins = new Float64Array(180);
  // Each sampled edge pixel's direction, to see afterwards whether those of a direction line up.
  const sampled = new Int32Array(Math.ceil(edges.length / stride));
  const sampledBin = new Int16Array(sampled.length).fill(-1);
  let samples = 0;
  let total = 0;

  for (let sample = 0; sample < edges.length; sample += stride) {
    const index = edges[sample];
    const x = index % width;
    const y = Math.floor(index / width);
    let xx = 0;
    let xy = 0;
    let yy = 0;

    for (let py = Math.max(0, y - 2); py <= Math.min(height - 1, y + 2); py += 1) {
      for (let px = Math.max(0, x - 2); px <= Math.min(width - 1, x + 2); px += 1) {
        const ux = gx[(py * width) + px];
        const uy = gy[(py * width) + px];
        xx += ux * ux;
        xy += ux * uy;
        yy += uy * uy;
      }
    }

    const energy = xx + yy;
    if (energy > 0) {
      const coherence = Math.hypot(xx - yy, 2 * xy) / energy;
      // The gradient crosses the line; the line runs a quarter turn from it.
      const across = (0.5 * Math.atan2(2 * xy, xx - yy)) / RADIANS;
      const along = (((Math.round(across + 90) % 180) + 180) % 180);
      bins[along] += coherence;
      total += coherence;
      sampled[samples] = index;
      sampledBin[samples] = along;
    }
    samples += 1;
  }

  /**
   * How much of a direction's edge lies in long lines: its edge pixels measured across the
   * direction, and those that fall with at least `minLine` pixels of edge on one line. A wall's
   * edge does; the notches grain leaves along a level edge, which read as a few degrees off level
   * wherever they are, do not.
   */
  const inLines = (degrees: number): number => {
    const radians = degrees * RADIANS;
    const [nx, ny] = [-Math.sin(radians), Math.cos(radians)];
    const offsets = new Map<number, number>();
    let near = 0;

    for (let sample = 0; sample < samples; sample += 1) {
      const bin = sampledBin[sample];
      const apart = Math.abs(bin - degrees);
      if (bin >= 0 && Math.min(apart, 180 - apart) <= 3) {
        const index = sampled[sample];
        // Two pixels a line, so an edge drawn in steps is still one line.
        const offset = Math.round((((index % width) * nx) + (Math.floor(index / width) * ny)) / 2);
        offsets.set(offset, (offsets.get(offset) ?? 0) + 1);
        near += 1;
      }
    }

    let lined = 0;
    offsets.forEach((pixels) => { if (pixels * stride >= minLine) lined += pixels; });
    return near ? lined / near : 0;
  };

  if (total <= 0) return [];

  const smooth = bins.map((value, bin) => ((bins[(bin + 179) % 180] + (2 * value) + bins[(bin + 1) % 180]) / 4));
  // A margin under a few degrees lets a peak by level or plumb through, whose middle may then fall
  // either side of 0: angles are kept 0 to 180 below, however near it.
  const offAxis = (bin: number): boolean => Math.min(bin % 90, 90 - (bin % 90)) >= Math.min(45, Math.max(0, axisMargin));
  const peaks: LineDirection[] = [];

  smooth.forEach((value, bin) => {
    const isPeak = [1, 2, 3, 4, 5, 6]
      .every((offset) => value >= smooth[(bin + offset) % 180] && value > smooth[(bin + 180 - offset) % 180]);
    if (!isPeak || !offAxis(bin)) return;

    // The share and the exact angle of the whole peak, not only its middle bin.
    let [weight, sum] = [0, 0];
    for (let offset = -3; offset <= 3; offset += 1) {
      const count = bins[(bin + offset + 180) % 180];
      weight += count;
      sum += count * offset;
    }

    const degrees = (((Math.round((bin + (sum / weight)) * 10) / 10) % 180) + 180) % 180;
    if (weight / total >= minShare && inLines(degrees) >= LINED_SHARE) {
      peaks.push({ degrees, share: weight / total });
    }
  });

  return peaks.sort((a, b) => b.share - a.share).slice(0, most);
};

/** How far a turn of `degrees` lines the ink up in rows and columns: sharper profiles score higher. */
const alignmentScore = (points: Float64Array, count: number, degrees: number, reach: number): number => {
  const cos = Math.cos(degrees * RADIANS);
  const sin = Math.sin(degrees * RADIANS);
  const size = (2 * reach) + 3;
  const rows = new Int32Array(size);
  const columns = new Int32Array(size);

  for (let index = 0; index < count; index += 1) {
    const x = points[index * 2];
    const y = points[(index * 2) + 1];
    rows[Math.round((x * sin) + (y * cos)) + reach + 1] += 1;
    columns[Math.round((x * cos) - (y * sin)) + reach + 1] += 1;
  }

  let score = 0;
  for (let index = 0; index < size; index += 1) {
    score += (rows[index] * rows[index]) + (columns[index] * columns[index]);
  }
  return score;
};

/** The most ink pixels the skew is estimated from: plenty for a sharp answer, and quick. */
const SKEW_SAMPLES = 30000;

/**
 * The turn, within `range` degrees, that lines the drawing's walls up with the picture's edges
 * — what a photo taken at a slight angle, or a scan fed in crooked, needs. 0 when the drawing
 * is already square, or has nothing straight enough to tell.
 */
export const skewCorrectionOf = (bitmap: Bitmap, range = 20): number => {
  const { width, height, data } = bitmap;
  let inkCount = 0;
  for (let index = 0; index < data.length; index += 1) inkCount += data[index];

  if (inkCount < 50) {
    return 0;
  }

  const stride = Math.max(1, Math.ceil(inkCount / SKEW_SAMPLES));
  const points = new Float64Array(Math.ceil(inkCount / stride) * 2 + 2);
  let count = 0;
  let seen = 0;

  for (let index = 0; index < data.length; index += 1) {
    if (data[index]) {
      if (seen % stride === 0) {
        const x = index % width;
        points[count * 2] = x - (width / 2);
        points[(count * 2) + 1] = ((index - x) / width) - (height / 2);
        count += 1;
      }
      seen += 1;
    }
  }

  // Every turned point lies within half the diagonal of the middle.
  const reach = Math.ceil(Math.hypot(width, height) / 2) + 1;

  const search = (from: number, to: number, step: number): number => {
    let best = 0;
    let bestScore = -1;

    for (let degrees = from; degrees <= to + 1e-9; degrees += step) {
      const score = alignmentScore(points, count, degrees, reach);
      if (score > bestScore) {
        bestScore = score;
        best = degrees;
      }
    }

    return best;
  };

  // The whole quarter turn is searched, because the score repeats every 90 degrees: a drawing
  // whose walls best line up further round than `range` is drawn at that angle, not photographed
  // crooked, and is left as it is — rather than turned by the most the range allows.
  const rough = search(-45, 45, 0.5);
  if (Math.abs(rough) > range) return 0;

  const fine = search(rough - 0.5, rough + 0.5, 0.1);
  const level = alignmentScore(points, count, 0, reach);
  const turned = alignmentScore(points, count, fine, reach);

  // Only worth turning for: a clear improvement, not noise in the score.
  return turned > level * 1.03 && Math.abs(fine) <= range ? Math.round(fine * 10) / 10 : 0;
};

/** The length of every unbroken run of ink along the rows and along the columns. */
export const runLengthsOf = (bitmap: Bitmap): number[] => {
  const { width, height, data } = bitmap;
  const lengths: number[] = [];

  const collect = (lines: number, length: number, at: (line: number, position: number) => number): void => {
    for (let line = 0; line < lines; line += 1) {
      let run = 0;

      for (let position = 0; position <= length; position += 1) {
        if (position < length && data[at(line, position)]) {
          run += 1;
        } else if (run > 0) {
          lengths.push(run);
          run = 0;
        }
      }
    }
  };

  collect(height, width, (line, position) => (line * width) + position);
  collect(width, height, (line, position) => (position * width) + line);
  return lengths;
};

/**
 * `percentileOf(runLengthsOf(bitmap).filter((length) => length >= shortest), fraction)`, counted
 * rather than listed and sorted: the same answer, for a picture's worth of runs, at a fraction of
 * the work.
 */
export const runLengthPercentileOf = (bitmap: Bitmap, fraction: number, shortest = 1): number => {
  const { width, height, data } = bitmap;
  const counts = new Float64Array(Math.max(width, height) + 2);
  let total = 0;

  const count = (run: number): void => {
    if (run >= shortest) {
      counts[run] += 1;
      total += 1;
    }
  };

  for (let y = 0; y < height; y += 1) {
    let run = 0;
    for (let index = y * width, end = index + width; index < end; index += 1) {
      if (data[index]) {
        run += 1;
      } else if (run > 0) {
        count(run);
        run = 0;
      }
    }
    if (run > 0) count(run);
  }

  const runs = new Int32Array(width);
  for (let y = 0; y < height; y += 1) {
    for (let x = 0, index = y * width; x < width; x += 1, index += 1) {
      if (data[index]) {
        runs[x] += 1;
      } else if (runs[x] > 0) {
        count(runs[x]);
        runs[x] = 0;
      }
    }
  }
  runs.forEach((run) => { if (run > 0) count(run); });

  if (!total) return 0;
  const rank = Math.min(total - 1, Math.floor(fraction * total));
  let seen = 0;
  for (let length = 0; length < counts.length; length += 1) {
    seen += counts[length];
    if (seen > rank) return length;
  }
  return counts.length - 1;
};

export const percentileOf = (values: readonly number[], fraction: number): number => {
  if (!values.length) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  return sorted[Math.min(sorted.length - 1, Math.floor(fraction * sorted.length))];
};

export const medianOf = (values: readonly number[]): number => percentileOf(values, 0.5);
